import { getPricingInfo } from '@thd-olt-component-react/product-set';
import {
  CHECKOUT_ADD_ON_CATEGORIES,
  CHECKOUT_ADD_ON_TYPES,
  INSTALL_ITEM_CONFIGURED,
  POST_MESSAGE,
  FULFILLMENT_METHOD,
  INSTALLATION_REQUIRED,
  SELECTED_INSTALLATION,
  STARTING_FROM,
  HAUL_AWAY_AND_MOVE,
  HAUL_AWAY,
  MOVE,
  CONFIG_ATTRS,
  CONFIG_ATTRS_CHEAPEST_IGNORE
} from './constants';
import { track } from './parts-and-services-analytics';

export const updateCart = () => {
  if (window && window.top && window.top.LIFE_CYCLE_EVENT_BUS) {
    window.top.LIFE_CYCLE_EVENT_BUS.trigger('cartUpdated', 'true');
  }
};

export const postMessageToParent = ({ message }) => {
  if (typeof window !== 'undefined') {
    window.parent.postMessage(message, '*');
  }
};

export const capitalize = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getInstallationDetails = ({ addOns }) => {
  return {
    hasHookup: addOns?.some(({ configAttr }) => configAttr?.toUpperCase() === 'HOOKUP'),
    hasInstallation: addOns?.some(({ installation }) => installation),
    hasTwoStepInstallation: addOns?.some(({ twoStepInstallation }) => twoStepInstallation)
  };
};

export const isEmptyObject = (obj) => {
  return Object.keys(obj).length === 0 && obj.constructor === Object;
};

export const isSelected = ({ selections, lineItemId, configAttr }) => {
  return !!selections[lineItemId]?.some((item) => item?.configAttr === configAttr);
};

export const filterByCategoryAndType = ({
  addOns,
  addOnCategories,
  addOnTypes
}) => {
  return addOns?.filter(({ category, type }) => (addOnCategories.includes(category)
    && (!addOnTypes || addOnTypes.includes(type))));
};

export const getAddOns = ({ productsMap, lineItemId, addOnCategories, addOnTypes }) => {
  return filterByCategoryAndType({
    addOns: [...productsMap.entries()]
      .map(([id, item]) => {
        return {
          ...item,
          lineItemId: id
        };
      }).find((entry) => entry.lineItemId === lineItemId)?.addOns,
    addOnCategories,
    addOnTypes
  });
};

// Analytics helper functions
const hasProtectionSelection = (addOns) => {
  return addOns?.some(({ category, selected }) => category === 'protectionPlan' && selected) || false;
};

const hasInstallationSelection = (addOns) => {
  return addOns?.some(
    ({ configAttr, selected, installation }) => (configAttr === CONFIG_ATTRS.HOOKUP && selected && installation)
      || (configAttr !== CONFIG_ATTRS.CONFIGURED && selected && installation)
  ) || false;
};

const hasPartsSelection = (addOns) => {
  return addOns?.some(({ type, selected }) => type === 'Part' && selected) || false;
};

const getHaulOption = (addOns) => {
  const selected = addOns?.filter(({ category, selected: sel }) => category === 'optional' && sel) ?? [];
  return selected.some(({ configAttr }) => configAttr === CONFIG_ATTRS.HAUL_AWAY) ? 'y' : 'n';
};

const getMoveOption = (addOns) => {
  const selected = addOns?.filter(({ category, selected: sel }) => category === 'optional' && sel) ?? [];
  return selected.some(({ configAttr }) => configAttr === CONFIG_ATTRS.MOVE) ? 'y' : 'n';
};

const buildAnalyticsData = (item, typeOfSelection) => {
  if (item.wfTest) {
    return { haulOption: 'filter', info: item.info };
  }

  const data = {
    hasProtection: hasProtectionSelection(item.addOns),
    hasInstallation: hasInstallationSelection(item.addOns),
    hasParts: hasPartsSelection(item.addOns),
    haulOption: getHaulOption(item.addOns),
    moveOption: getMoveOption(item.addOns),
    info: item.info
  };

  if (typeOfSelection === 'package') {
    data.isPackage = true;
  } else if (typeOfSelection === 'bundle') {
    data.isBundle = true;
  }

  return data;
};

export const ctaAnalytics = ({
  newItemsArray,
  newAddOnsArrays,
  newProductsArray,
  currentPage,
  isEnableCompatibleAccessoriesPAS,
  typeOfSelection,
  displayed
}) => {
  const combinedData = newItemsArray.map((ele, index) => ({
    info: ele,
    addOns: newAddOnsArrays[index]
  }));

  if (newProductsArray && newProductsArray.length > 0) {
    newProductsArray.forEach((productInfo) => {
      combinedData.push({ info: productInfo, wfTest: isEnableCompatibleAccessoriesPAS });
    });
  }

  const analyticsData = combinedData.map((item) => buildAnalyticsData(item, typeOfSelection));

  // Track analytics based on current page
  if (currentPage === 'protection') {
    track('atc-hdpp', analyticsData);
  }

  if (currentPage === 'services') {
    track(displayed ? 'displayed-installation-haulaway' : 'atc-installation-haulaway', analyticsData);
  }
};

const userHasDeclinedInstallation = ({ installationSelectionsForItem = [] }) => {
  return installationSelectionsForItem?.some(
    (addOnSelection) => addOnSelection.category === INSTALL_ITEM_CONFIGURED.category
      && addOnSelection.configAttr === INSTALL_ITEM_CONFIGURED.configAttr
  ) || false;
};

export const getAddOnsAndNewItemsArraysObj = ({
  applianceStoreId,
  deliveryZip,
  currentPage,
  lineItemIds,
  itemIds,
  addOnsSelections,
  fulfillmentSelections,
  protectionSelections,
  installationSelections,
  haulAwaySelections,
  products,
  newProductSelections,
  disableNavigation = false,
  isCheckoutFlow = false,
  addDirectlyToCart = false
}) => {
  const newItemsArray = [];
  const newAddOnsArrays = [];
  const newProductsArray = [];

  const selectedConfigAttrs = lineItemIds?.reduce((sorted, lineItemId) => {
    if (disableNavigation && !isCheckoutFlow) {
      if (currentPage === 'protection') {
        // eslint-disable-next-line no-param-reassign
        sorted[lineItemId] = [
          protectionSelections[lineItemId]?.configAttr,
          ...(addOnsSelections?.installation?.[lineItemId]?.map((configAttr) => configAttr) ?? []),
          ...(addOnsSelections?.haulAway?.[lineItemId]?.map((configAttr) => configAttr) ?? [])
        ];
      } else if (currentPage === 'services') {
        // eslint-disable-next-line no-param-reassign
        sorted[lineItemId] = [
          addOnsSelections?.protection[lineItemId],
          ...(installationSelections?.[lineItemId]?.map(({ configAttr }) => configAttr) ?? []),
          ...(haulAwaySelections?.[lineItemId]?.map(({ configAttr }) => configAttr) ?? [])
        ];
      }
    } else if (addDirectlyToCart) {
      // eslint-disable-next-line no-param-reassign
      sorted[lineItemId] = [
        addOnsSelections?.protection[lineItemId],
        ...(addOnsSelections?.installation?.[lineItemId]?.map((configAttr) => configAttr) ?? []),
        ...(addOnsSelections?.haulAway?.[lineItemId]?.map((configAttr) => configAttr) ?? [])
      ];
    } else {
      // eslint-disable-next-line no-param-reassign
      sorted[lineItemId] = [
        protectionSelections[lineItemId]?.configAttr,
        ...(installationSelections?.[lineItemId]?.map(({ configAttr }) => configAttr) ?? []),
        ...(haulAwaySelections?.[lineItemId]?.map(({ configAttr }) => configAttr) ?? [])
      ];
    }
    return sorted;
  }, {});

  Object.values(newProductSelections).flat().forEach((product) => {
    newProductsArray.push({
      item: {
        store: applianceStoreId,
        fulfillmentLocation: deliveryZip,
        fulfillmentMethod: FULFILLMENT_METHOD.STH,
        product
      },
      itemId: product.itemId,
      quantity: 1,
      fulfillmentLocation: deliveryZip,
      fulfillmentMethod: FULFILLMENT_METHOD.STH
    });
  });

  lineItemIds.forEach((lineItemId, index) => {
    const newProduct = { ...products?.find((product) => product.itemId === itemIds[index]) };

    newAddOnsArrays.push((newProduct?.addOns || []).map((addOn) => {
      const { category, configAttr: addOnConfigAttr, installation, type } = addOn;
      const isSelection = selectedConfigAttrs[lineItemId]?.includes(addOnConfigAttr);
      const configAttr = (!isSelection && addOnConfigAttr === 'Hookup') ? 'Configured' : addOnConfigAttr;
      const selected = isSelection || configAttr === 'Configured';
      return {
        selected,
        category,
        configAttr,
        installation,
        type
      };
    }));

    if (
      userHasDeclinedInstallation({ installationSelectionsForItem: installationSelections[lineItemId] })
    ) {
      newAddOnsArrays[index].push({ ...INSTALL_ITEM_CONFIGURED, selected: true });
    }

    delete newProduct.addOns;

    newItemsArray.push({
      item: {
        store: applianceStoreId,
        id: lineItemId,
        selectedFulfillment: fulfillmentSelections[lineItemId],
        fulfillmentLocation: deliveryZip,
        quantity: 1,
        product: {
          ...newProduct,
        }
      },
      fulfillmentLocation: deliveryZip,
      fulfillmentMethod: fulfillmentSelections[lineItemId],
    });
  });
  return {
    newItemsArray,
    newAddOnsArrays,
    newProductsArray
  };
};

export const postSelectionsToCart = ({
  onSubmit = () => {},
  currentPage = '',
  updateItemsAddOns = () => {},
  enableCheckoutEvent = false,
  newItemsArray = [],
  newAddOnsArrays = [],
  newProductsArray = [],
  handleAddProductsToCart = () => {},
  typeOfSelection = null
}) => {
  if (updateItemsAddOns) {
    ctaAnalytics({
      newItemsArray,
      newAddOnsArrays,
      newProductsArray,
      currentPage,
      typeOfSelection
    });

    updateItemsAddOns(newItemsArray, newAddOnsArrays, onSubmit)
      .then(() => {
        if (newProductsArray.length > 0 && !!handleAddProductsToCart) {
          handleAddProductsToCart(newProductsArray);
        }
      })
      .then(() => {
        if (enableCheckoutEvent && currentPage === 'services') postMessageToParent({ message: POST_MESSAGE.CHECKOUT });
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error during update:', error);

        onSubmit({ action: 'save_failed' });
      });
  }
};

const compareByCategoryAndType = (
  category, type, addOnCategories, addOnTypes
) => addOnCategories.includes(category) && addOnTypes.includes(type);

const filterAddOns = (addOns) => {
  return addOns.reduce((filteredAddOns, addOn) => {
    const { category, type } = addOn;

    if (compareByCategoryAndType(
      category, type, CHECKOUT_ADD_ON_CATEGORIES.PROTECTION, CHECKOUT_ADD_ON_TYPES.PROTECTION
    )) {
      filteredAddOns.protection.push(addOn);
    } else if (compareByCategoryAndType(
      category, type, CHECKOUT_ADD_ON_CATEGORIES.INSTALLATION, CHECKOUT_ADD_ON_TYPES.INSTALLATION
    )) {
      filteredAddOns.installation.push(addOn);
    } else if (compareByCategoryAndType(
      category, type, CHECKOUT_ADD_ON_CATEGORIES.CONFIG, CHECKOUT_ADD_ON_TYPES.CONFIG
    )) {
      filteredAddOns.config.push(addOn);
    } else if (compareByCategoryAndType(
      category, type, CHECKOUT_ADD_ON_CATEGORIES.REQUIRED, CHECKOUT_ADD_ON_TYPES.REQUIRED
    )) {
      filteredAddOns.required.push(addOn);
    } else if (compareByCategoryAndType(
      category, type, CHECKOUT_ADD_ON_CATEGORIES.HAUL_AWAY, CHECKOUT_ADD_ON_TYPES.HAUL_AWAY
    )) {
      filteredAddOns.haulAway.push(addOn);
    }

    return filteredAddOns;
  }, {
    protection: [],
    installation: [],
    config: [],
    required: [],
    haulAway: []
  });
};

const getConfigAttrs = (items) => {
  return items?.map(({ configAttr }) => configAttr);
};

const getAddOnsConfigs = ({
  protection: protectionItems,
  installation: installationItems,
  required: requiredItems,
  haulAway: haulAwayItems,
  config,
  lineItemId
}) => {
  const protection = { [lineItemId]: getConfigAttrs(protectionItems)[0] ?? '' };

  const installation = {
    [lineItemId]: [
      ...(getConfigAttrs(installationItems) ?? []),
      ...(getConfigAttrs(requiredItems) ?? [])
    ]
  };

  if (config[0]?.category === 'applianceConfig' && config[0]?.configAttr === 'Configured') {
    installation[lineItemId].push('Configured');
  }

  if (config[0]?.category === 'applianceConfig' && config[0]?.configAttr === 'Hookup') {
    installation[lineItemId].push('Hookup');
  }

  const haulAway = { [lineItemId]: getConfigAttrs(haulAwayItems) ?? [] };

  return {
    protection,
    installation,
    haulAway
  };
};

export const getAddOnsFromCartModel = ({ items }) => {
  let addOnsCombined = {
    haulAway: {},
    protection: {},
    installation: {}
  };

  items.forEach((item) => {
    const {
      protection,
      installation,
      config,
      required,
      haulAway,
      optional
    } = filterAddOns(item?.product?.addons);

    const { id: lineItemId } = item;

    const addOns = getAddOnsConfigs({
      protection,
      installation,
      required,
      haulAway,
      optional,
      config,
      lineItemId
    });

    Object.assign(addOnsCombined.haulAway, addOns.haulAway);
    Object.assign(addOnsCombined.protection, addOns.protection);
    Object.assign(addOnsCombined.installation, addOns.installation);
  });

  return addOnsCombined;
};

/* eslint-disable no-param-reassign */
export const getFulfillmentSelections = ({ items }) => {
  return items.reduce((fulfillmentSelections, item) => {
    fulfillmentSelections[item.id] = item.selectedFulfillment;
    return fulfillmentSelections;
  }, {});
};

const reduceUnitPricesCallback = (acc, item) => ((item ? Number(item.price || 0) : 0) + acc);

export const getPricingObjectForTotal = ({
  productsMap,
  enableApplianceDeliveryCharge,
  enableFreeDeliveryForExchange,
  applianceDeliveryChargeValue,
  isExchangeCustomer,
  haulAwaySelections,
  protectionSelections,
  installationSelections,
  newProductSelections
}) => {
  const haulAwayTotal = Object.values(haulAwaySelections)
    .reduce((acc, itemSelections) => {
      return acc + itemSelections.reduce(reduceUnitPricesCallback, 0);
    }, 0);
  const protectionTotal = Object.values(protectionSelections).reduce(reduceUnitPricesCallback, 0);
  const installationTotal = Object.values(installationSelections)
    .reduce((acc, itemSelections) => {
      return acc + itemSelections.reduce(reduceUnitPricesCallback, 0);
    }, 0);

  const arrayOfProducts = Array.from(productsMap.values());
  const productsCopy = JSON.parse(JSON.stringify(arrayOfProducts));
  const pricingArray = productsCopy.map((item) => item);
  const pricingArrayWithProducts = (Object.values(newProductSelections) ?? []).flat();
  const combinedPricingArray = [...pricingArray, ...pricingArrayWithProducts];

  if (arrayOfProducts?.length && arrayOfProducts[0]?.pricing) {
    const {
      originalPrice,
      dollarOffPromo,
      dollarOff,
      totalPrice,
      mapAboveOriginalPrice,
      mapDetail,
      hideTotal
    } = getPricingInfo(
      combinedPricingArray,
      enableApplianceDeliveryCharge,
      enableFreeDeliveryForExchange,
      applianceDeliveryChargeValue,
      isExchangeCustomer
    );

    return {
      pricing: {
        promotion: {
          dollarOff: dollarOff + dollarOffPromo,
          percentageOff: ((dollarOff + dollarOffPromo) * 100) / originalPrice
        },
        value: (totalPrice + protectionTotal + installationTotal + haulAwayTotal),
        mapAboveOriginalPrice,
        mapDetail,
        hideTotal
      }
    };
  }
  return null;
};

export const isHookup = (installation) => {
  return installation?.find((install) => install?.configAttr === 'Hookup');
};

export const getPartPrice = (installation) => {
  return installation?.find((install) => install?.category === 'required')?.price;
};

export const getHaulAwayPrice = (haulAway) => {
  return haulAway?.find((haul) => haul?.configAttr === '174602')?.price || 0;
};

export const getMovePrice = (haulAway) => {
  return haulAway?.find((move) => move?.configAttr === '853290')?.price || 0;
};

export const getCheapestItem = (items) => {
  if (!items?.length) {
    return null;
  }
  return items.reduce((min, item) => {
    if (CONFIG_ATTRS_CHEAPEST_IGNORE.includes(item.configAttr)) {
      return min;
    }
    const itemPrice = parseFloat(item.price);
    if (Number.isNaN(itemPrice)) {
      return min;
    }
    if (!min) {
      return item;
    }
    return itemPrice < parseFloat(min.price) ? item : min;
  }, null);
};

export const hasUserDeclinedProtection = (selection) => {
  return !selection || !selection.termLength;
};

export const hasUserDeclinedHaulAwayAndMove = (selection) => {
  const haulAwayPrice = getHaulAwayPrice(selection);
  const movePrice = getMovePrice(selection);
  return !haulAwayPrice && !movePrice;
};

export const hasUserDeclinedInstallation = (selection) => {
  return !getPartPrice(selection);
};

// Calculate lowest prices from items arrays
export const getLowestProtectionPrice = (protectionItems) => {
  return protectionItems.length > 0
    ? Math.min(...protectionItems.map((item) => item?.price || 0))
    : 0;
};

export const getLowestInstallPrice = (installationItems) => {
  return installationItems.find((item) => item?.category === 'required')?.price || 0;
};

export const getLowestHaulAwayPrice = (haulAwayItems) => {
  return haulAwayItems.length > 0
    ? Math.min(...haulAwayItems.map((item) => item?.price || 0))
    : 0;
};

// Check if hookup installation is available in items
export const isHookupAvailable = (installationItems) => {
  return installationItems.find((install) => install?.configAttr === 'Hookup');
};

// Get text labels based on selection state
export const getInstallationTextLabel = (hasUserSelectedInstallation, isHookupInstallation) => {
  if (!hasUserSelectedInstallation) {
    return INSTALLATION_REQUIRED;
  }
  return isHookupInstallation ? SELECTED_INSTALLATION : INSTALLATION_REQUIRED;
};

export const getHaulAwayTextLabel = (hasUserSelectedHaulAway, totalPrice, haulAwayPrice, movePrice) => {
  if (!hasUserSelectedHaulAway || !totalPrice) {
    return STARTING_FROM;
  }
  if (haulAwayPrice && movePrice) {
    return HAUL_AWAY_AND_MOVE;
  }
  if (haulAwayPrice) {
    return HAUL_AWAY;
  }
  if (movePrice) {
    return MOVE;
  }
  return STARTING_FROM;
};

export const getProtectionTextLabel = (hasUserSelectedProtection, protection) => {
  return hasUserSelectedProtection && protection?.termLength
    ? `Selected: ${protection.termLength} Year Protection Plan`
    : STARTING_FROM;
};

export const isExcludedProduct = (productInfo, excludedTypes) => {
  const { classNumber, subClassNumber, productDepartment } = productInfo || {};
  if (!productInfo || productDepartment == null || classNumber == null || subClassNumber == null || !excludedTypes) {
    return false;
  }

  const actualProductKey = `${productDepartment}-${classNumber}-${subClassNumber}`;

  const excludedCategories = excludedTypes.split(',').map((category) => category.trim());

  return excludedCategories.includes(actualProductKey);
};
// Consolidated calculation for DropDownButton
export const calculateDropDownButtonData = ({
  protection,
  installation,
  haulAway,
  hasUserSelectedProtection,
  hasUserSelectedHaulAway,
  hasUserSelectedInstallation,
  protectionItems,
  installationItems,
  haulAwayItems
}) => {
  // Calculate lowest prices
  const lowestProtectionPrice = getLowestProtectionPrice(protectionItems);
  const lowestInstallPrice = getLowestInstallPrice(installationItems);
  const lowestHaulAwayPrice = getLowestHaulAwayPrice(haulAwayItems);

  // Check if hookup installation is available or selected
  const isHookupInstallation = hasUserSelectedInstallation
    ? isHookup(installation)
    : isHookupAvailable(installationItems);

  // Calculate prices based on selection state
  const partPrice = hasUserSelectedInstallation ? getPartPrice(installation) : lowestInstallPrice;
  const protectionPrice = hasUserSelectedProtection ? protection?.price : lowestProtectionPrice;
  const haulAwayPrice = hasUserSelectedHaulAway ? getHaulAwayPrice(haulAway) : 0;
  const movePrice = hasUserSelectedHaulAway ? getMovePrice(haulAway) : 0;
  const totalHaulAwayAndMovePrice = hasUserSelectedHaulAway
    ? haulAwayPrice + movePrice
    : lowestHaulAwayPrice;

  // Get text labels
  const installationText = getInstallationTextLabel(hasUserSelectedInstallation, isHookupInstallation);
  const haulAwayText = getHaulAwayTextLabel(
    hasUserSelectedHaulAway,
    totalHaulAwayAndMovePrice,
    haulAwayPrice,
    movePrice
  );
  const protectionText = getProtectionTextLabel(hasUserSelectedProtection, protection);

  return {
    isHookupInstallation,
    prices: {
      services: partPrice,
      haulAway: totalHaulAwayAndMovePrice,
      protection: protectionPrice
    },
    labels: {
      services: installationText,
      haulAway: haulAwayText,
      protection: protectionText
    }
  };
};

export const getProducts = (data) => {
  return data?.getPartsServices?.product.map((product) => {
    return {
      ...product,
      addOns: product.addOns.map((addOn) => {
        const { category, type } = addOn;
        if (category === 'applianceConfig' && type === 'Install') {
          return {
            ...addOn,
            category: 'required'
          };
        }
        return addOn;
      }
      )
    };
  });
};

// Determine page configuration for PartsAndServices component
export const determinePageConfiguration = ({
  enableCheckoutEvent,
  isProtection,
  hasServicesToShow,
  hasProtectionsToShow,
  disableNavigation,
  disableUpdateCart,
  enableInstallationFirst,
  showAlert,
  hasDropDownSelections,
  confirmationEnabled,
  isConfirmation
}) => {
  const config = {
    ctaButtonKeys: [],
    onBack: null,
    headerText: '',
    showSuccessCheck: false,
    isCheckoutFlow: false,
    addDirectlyToCart: false
  };

  // Confirmation Drawer scenarios
  if (confirmationEnabled) {
    if (hasDropDownSelections) {
      config.addDirectlyToCart = true;
    }
    if (isConfirmation) {
      config.ctaButtonKeys = ['viewCart'];
      config.headerText = 'Added To Cart';
      config.showSuccessCheck = true;
    }
    return config;
  }

  // Checkout event scenarios
  if (enableCheckoutEvent) {
    if (isProtection && hasServicesToShow) {
      config.ctaButtonKeys = ['continueToInstallation'];
      config.headerText = 'Home Depot Protection Plan';
    } else if (isProtection && !hasServicesToShow) {
      config.ctaButtonKeys = ['continueToCheckout'];
      config.headerText = 'Home Depot Protection Plan';
      config.isCheckoutFlow = true;
    } else {
      config.ctaButtonKeys = ['continueToCheckout'];
      config.headerText = 'Installation & Haul Away Services';
      config.isCheckoutFlow = true;
    }
    return config;
  }

  // Disabled navigation scenarios
  if (disableNavigation) {
    config.ctaButtonKeys = disableUpdateCart ? ['saveAndKeepShopping'] : ['save'];
    config.headerText = isProtection ? 'Home Depot Protection Plan' : 'Installation & Haul Away Services';
    return config;
  }

  // Normal navigation scenarios
  if (showAlert) {
    config.headerText = isProtection ? 'Home Depot Protection Plan' : 'Installation & Haul Away Services';
  } else {
    config.headerText = 'Added To Cart';
  }
  config.showSuccessCheck = !showAlert;

  if (isProtection) {
    if (enableInstallationFirst) {
      config.ctaButtonKeys = ['continueToCart', 'saveAndKeepShopping'];
      config.onBack = 'navigateToServices';
    } else {
      config.ctaButtonKeys = ['continueToInstallation', 'saveAndKeepShopping'];
    }
  } else if (enableInstallationFirst) {
    config.ctaButtonKeys = ['continueToProtection', 'saveAndKeepShopping'];
  } else {
    config.ctaButtonKeys = ['continueToCart', 'saveAndKeepShopping'];
    config.onBack = 'navigateToProtection';
  }

  return config;
};

export const findItemByConfigAttr = (items, targetConfigAttr) => {
  return items.find(({ configAttr }) => configAttr === targetConfigAttr);
};

export const findItemByConfigAttrIgnoreCase = (items, targetConfigAttr) => {
  return items.find(({ configAttr }) => configAttr?.toUpperCase() === targetConfigAttr.toUpperCase());
};